import { useMemo } from 'react';

import {
  DealItem,
  DealItemWithVersions,
  ValidationError,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { Row } from '@tanstack/react-table';
import { atom, useAtomValue } from 'jotai';

/**
 * Maps an editable cell field to the validation rules that should paint that
 * cell red. Both rules are really "the Choose price is outside the
 * [buyingPrice, listPrice] band", so the Choose price is flagged for both,
 * and the derived percentage (discount / margin) is flagged for its own rule.
 */
const FIELD_VALIDATION_RULES: Partial<
  Record<keyof DealItem, ValidationRuleId[]>
> = {
  choosePrice: [
    ValidationRuleId.NegativeMargin,
    ValidationRuleId.DiscountConsistency,
  ],
  marginPercentage: [ValidationRuleId.NegativeMargin],
  chooseDiscount: [ValidationRuleId.DiscountConsistency],
};

/**
 * Pure detection of a validation-rule error for a given cell field, from a flat
 * list of the row's effective validation errors. Exported for unit tests.
 */
export function getCellRuleErrorState(
  errors: ValidationError[],
  field: keyof DealItem
): boolean {
  const rules = FIELD_VALIDATION_RULES[field];
  if (!rules || errors.length === 0) return false;
  return rules.some((ruleId) =>
    errors.some((error) => error.ruleId === ruleId)
  );
}

/**
 * Whether a cell should be painted red because its row carries a blocking
 * validation rule (negative_margin / discount_consistency). The error itself is
 * explained in the validation-errors column, so this only drives the red
 * background — no extra cell tooltip (which would clash with the existing
 * brand/Choose comparison tooltip on these fields).
 *
 * Reads `effectiveValidationErrorsAtom` (client worker + backend errors) so the
 * highlight reacts immediately to inline edits, but through a per-cell boolean
 * atom so only the cells whose state changes re-render. Non-selected items never
 * show an error, consistent with `getTableCellErrorState`; parent rows aggregate
 * their *selected* sub-rows.
 *
 * Use inside a module-level cell component (not directly in a `cell:` render).
 */
export function useCellValidationRuleError(
  row: Row<DealItemWithVersions>,
  field: keyof DealItem
): boolean {
  const isParent = row.subRows.length > 1;
  const isSelected = row.original.activeVersion.isSelected;

  const ids = !isSelected
    ? []
    : isParent
      ? row.subRows
          .filter((subRow) => subRow.original.activeVersion.isSelected)
          .map((subRow) => subRow.original.id)
      : [row.original.id];

  const idsKey = ids.join(',');
  const cellAtom = useMemo(
    () =>
      atom((get): boolean => {
        if (idsKey === '') return false;
        const effectiveErrors = get(effectiveValidationErrorsAtom);
        const errors = idsKey
          .split(',')
          .flatMap((id) => effectiveErrors.get(id) ?? []);
        return getCellRuleErrorState(errors, field);
      }),
    [field, idsKey]
  );

  return useAtomValue(cellAtom);
}
